/**
 * DateTime Field Component
 * Wrapper for CustomDateTimePicker with react-hook-form integration
 */
import React, { useCallback, useEffect } from 'react'
import { Controller } from 'react-hook-form'
import { FormFieldProps } from '../types'
import { useFormContext } from '../FormContext'
import {
  Box,
  IconButton,
  TextField,
  FormControl,
  InputLabel
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import ClearIcon from '@mui/icons-material/Clear'
import dayjs from 'dayjs'
import { buildValidationRules } from '../utils/validationRules'

const DateTimeField: React.FC<FormFieldProps> = ({
  config,
  control,
  errors,
  readOnly
}) => {
  const {
    name,
    label,
    required,
    disabled,
    helperText,
    fieldProps = {}
  } = config

  // 从fieldProps中提取特定属性
  const {
    dateType = 'date',
    sx: fieldSx,
    InputLabelProps,
    placeholder,
    ...otherFieldProps
  } = fieldProps

  // check if the field is changed
  const formContext = useFormContext()
  const formValues = formContext?.values || {}

  // Clear date value on unmount.
  //
  // RHF's internal `field._f.refs` only exists for fields whose ref was
  // wired through (typed/native input registration). DateTime fields go
  // through `Controller`, which does NOT populate `_f.refs` — it tracks
  // value via `field.value` instead. Reaching for `.refs.forEach` blew up
  // with `Cannot read properties of undefined (reading 'forEach')` in
  // every test that mounted a DateTimeField. The previous code swallowed
  // it via try/catch + console.error; switch to optional chaining so the
  // happy path is silent and the catch only fires on truly unexpected
  // shapes.
  useEffect(() => {
    return () => {
      if (!control) return
      try {
        const refs = control._fields?.[name]?._f?.refs
        if (!refs) return
        refs.forEach((ref: { value?: unknown } | null | undefined) => {
          if (ref && typeof ref.value !== 'undefined') {
            ref.value = ''
          }
        })
      } catch (error) {
        console.error('Error clearing date field on unmount:', error)
      }
    }
  }, [control, name])

  const isFieldChanged = () => {
    if (config.enableChangeHighlight !== true) {
      return false
    }

    if (formContext) {
      const originalValues = formContext.originalValues || {}
      const currentValues = formValues

      // compare the original value and the current value
      const originalValue = originalValues[name]
      const currentValue = currentValues[name]

      // handle the string value
      const origStrValue = originalValue?.toString() || ''
      const currStrValue = currentValue?.toString() || ''

      // only return true when the value is really different
      return origStrValue !== currStrValue
    }
    return false
  }

  // check if the field is changed
  const isChanged = isFieldChanged()

  // check if the field is read only
  const isReadOnly =
    typeof readOnly === 'boolean'
      ? readOnly
      : Array.isArray(readOnly)
        ? readOnly.includes(name)
        : false

  // 选择使用哪个Picker组件
  const Picker = dateType === 'date' ? DatePicker : DateTimePicker

  // 自定义TextField渲染
  const renderTextField = useCallback(
    (params: any) => (
      <TextField
        {...params}
        variant='standard'
        placeholder={placeholder}
        InputProps={{
          ...params.InputProps,
          disableUnderline: true
        }}
        sx={{
          width: '100%'
        }}
      />
    ),
    [placeholder]
  )

  // 合并样式
  const mergedSx = {
    '& .MuiInputBase-root': {
      height: '36px'
    },
    ...(isChanged && {
      '& .MuiInputBase-input': {
        color: 'secondary.dark',
        fontWeight: 'bold'
      }
    }),
    ...(fieldSx || {})
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={buildValidationRules(config)}
      render={({ field }) => {
        const handleDateChange = (newValue: dayjs.Dayjs | null) => {
          const formattedValue = newValue
            ? dateType === 'date'
              ? newValue.format('YYYY-MM-DD')
              : newValue.format('YYYY-MM-DD HH:mm:ss')
            : ''

          field.onChange(formattedValue)
        }

        const handleClear = (event: React.MouseEvent) => {
          event.stopPropagation()
          handleDateChange(null)
        }

        return (
          <FormControl
            variant='outlined'
            fullWidth
            required={required}
            disabled={disabled || isReadOnly}
            error={!!errors[name]}
            sx={{
              '& .MuiFormLabel-root': {
                backgroundColor: 'white',
                px: 1,
                '&.Mui-focused': {
                  color: 'primary.main'
                }
              },
              ...mergedSx
            }}>
            <InputLabel shrink={InputLabelProps?.shrink !== false}>
              {label}
            </InputLabel>
            <Box
              onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
                const related = e.relatedTarget as HTMLElement | null
                // Skip blur when focus moves into the DatePicker popper (portal)
                if (related?.closest('.MuiPickersPopper-root')) return

                field.onBlur()
                if (formContext?.onBlur) {
                  formContext.onBlur(
                    { name, value: field.value },
                    formContext.values || {}
                  )
                }
              }}
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.87)'
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                minHeight: '36px',
                height: '36px',
                position: 'relative',
                ...(errors[name] ? { borderColor: 'error.main' } : {})
              }}>
              <Picker
                value={field.value ? dayjs(field.value) : null}
                onChange={handleDateChange}
                format={
                  dateType === 'date' ? 'YYYY/MM/DD' : 'YYYY/MM/DD HH:mm:ss'
                }
                closeOnSelect={true}
                slots={{
                  textField: renderTextField
                }}
                slotProps={{
                  popper: {
                    sx: {
                      '& .MuiPickersYear-yearButton': {
                        fontSize: '14px'
                      },
                      '& .MuiMultiSectionDigitalClock-root': {
                        '& .MuiMenuItem-root': {
                          fontSize: '14px',
                          padding: '4px 8px'
                        }
                      },
                      '& .MuiDialogActions-root': {
                        display: 'none'
                      }
                    }
                  },
                  actionBar: {
                    actions: []
                  }
                }}
                disabled={disabled || isReadOnly}
                {...otherFieldProps}
              />

              {field.value && !disabled && !isReadOnly && (
                <IconButton
                  size='small'
                  sx={{
                    position: 'absolute',
                    right: '35px',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    padding: 0.5,
                    ':hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    },
                    '.MuiBox-root:hover &': {
                      opacity: 1
                    }
                  }}
                  onClick={handleClear}>
                  <ClearIcon fontSize='small' />
                </IconButton>
              )}
            </Box>
            {(errors[name] || helperText) && (
              <Box
                sx={{
                  color: errors[name] ? 'error.main' : 'text.secondary',
                  fontSize: '0.75rem',
                  mt: 0.5,
                  ml: 1.5
                }}>
                {errors[name]?.message || helperText}
              </Box>
            )}
          </FormControl>
        )
      }}
    />
  )
}

export default DateTimeField
